/**
 * Every user-facing string lives here.
 *
 * Code, types and API fields are in English; only what the user reads is in
 * Spanish. Centralizing the strings means a wording change is one diff, and
 * it keeps Spanish out of the components — which is the rule that stops the
 * two languages from mixing as the app grows.
 */
export const es = {
  app: {
    name: 'Consulta Comercial GGI',
    brandAlt: 'Gigante Grupo Inmobiliario',
    shortName: 'GGI',
  },

  nav: {
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    hideSidebar: 'Ocultar menú',
    showSidebar: 'Mostrar menú',
    toggleTheme: 'Cambiar tema',
    plazas: 'Plazas',
    allPlazas: 'Todas las plazas',
    noPlazas: 'Sin plazas asignadas',
    morePlazas: '+{count} más',
    sections: {
      viewer: 'Planos',
      administration: 'Administración',
    },
    items: {
      viewer: 'Consulta de planos',
      users: 'Usuarios',
      roles: 'Roles y permisos',
      plans: 'Planos cargados',
      properties: 'Plazas',
      audit: 'Bitácora',
    },
  },

  session: {
    signOut: 'Cerrar sesión',
    account: 'Cuenta',
    notSignedIn: 'Sin sesión',
    devMode: 'Modo desarrollo · sin sesión',
    noDb: 'BD no configurada',
    noDbHint: 'El backend corre sin base de datos (modo desarrollo): solo tu cuenta de administrador de arranque tiene acceso y usuarios, roles y bitácora no están disponibles.',
    devModeHint: 'Microsoft Entra ID no está configurado: la app usa el usuario de desarrollo del backend (dev-bypass).',
    role: 'Rol',
  },

  auth: {
    login: {
      title: 'Consulta Comercial GGI',
      subtitle: 'Inicia sesión con tu cuenta institucional para consultar la disponibilidad de locales.',
      button: 'Iniciar sesión con Microsoft',
      retry: 'Reintentar',
      redirecting: 'Redirigiendo a Microsoft…',
    },
    errors: {
      loginFailed: 'No se pudo completar el inicio de sesión. Intenta de nuevo.',
      apiUnreachable: 'No se pudo conectar con el servidor ({url}). Verifica que el backend esté corriendo.',
    },
    accessRequest: {
      title: 'Solicita acceso al administrador',
      notRegistered: 'Tu cuenta se autenticó correctamente, pero todavía no está dada de alta en esta herramienta.',
      inactive: 'Tu cuenta está desactivada en esta herramienta.',
      accountLabel: 'Cuenta',
      instructions: 'Pide al administrador que registre o reactive este correo y vuelve a iniciar sesión.',
      signOut: 'Cerrar sesión',
    },
  },

  status: {
    available: 'Disponible',
    unavailable: 'No disponible',
    unknown: 'Sin información',
    // "Por estatus" blocks.
    occupied: 'Ocupado',
    legal: 'Legal',
    negotiation: 'Negociación',
  },

  viewer: {
    levels: 'Niveles del inmueble',
    availableShort: 'disp.',
    hint: 'Haz clic en un local para ver su detalle · rueda para zoom',
    zoom: {
      group: 'Controles de zoom',
      in: 'Acercar',
      out: 'Alejar',
      fit: 'Ajustar a la pantalla',
      level: 'Zoom {pct}%',
      minimap: 'Minimapa del plano: clic para centrar, arrastra el recuadro para mover',
    },
    layers: {
      button: 'Capas',
      title: 'Capas del plano',
      groups: {
        exterior: 'Exterior',
        landscape: 'Paisaje',
        structure: 'Estructura',
        circulation: 'Circulación',
        furniture: 'Mobiliario',
        vehicles: 'Vehículos',
        equipment: 'Equipos',
        labels: 'Rótulos',
      },
    },
    planLabel: 'plano',
    loadedAt: 'corte',
    loading: 'Cargando la información de renta…',
    loadError: 'No se pudo cargar la información de renta.',
    retry: 'Reintentar',
    noPlazas: 'Tu cuenta no tiene plazas asignadas. Solicita acceso al administrador.',
    noPublishedPlazas: 'Aún no hay plazas con plano publicado',
    rentRollUnavailable: 'La fuente de datos (BigQuery) no está disponible. El plano se muestra sin estatus.',
    rentRollEmpty: 'No hay datos de rent roll para esta plaza en el mes cerrado. El plano se muestra sin estatus.',
    noPublishedPlan: 'Esta plaza no tiene plano publicado',
    noPublishedPlanHint: 'Cuando se publique un plano de esta plaza aparecerá aquí.',
    goToPlans: 'Ir a Planos cargados',
    planError: 'No se pudo cargar el plano de esta plaza.',
    noData: 'Sin dato en la fuente',
    inReviewTag: 'En revisión',
    inReviewBanner: 'Versión en revisión — confirmar con negocio. Es la copia alterna del plano; compárala con la versión A del mismo nivel.',

    toolbar: {
      changePlaza: 'Cambiar plaza',
      searchPlaceholder: 'Buscar local o marca',
      status: 'Estatus',
      expiry: 'Vigencia',
      all: 'Todos',
      any: 'Cualquiera',
      expiringUpTo90: 'Vence en ≤ 90 días',
      expiringUpTo365: 'Vence en 91–365 días',
      expiringOverYear: 'Vence en > 1 año',
      noLease: 'Sin vigencia',
      viewMode: 'Modo de visualización',
      byAvailability: 'Disponible / No disponible',
      byStatus: 'Por estatus',
      clearFilters: 'Limpiar filtros',
    },

    kpis: {
      totalUnits: 'Locales totales',
      availableUnits: 'Locales disponibles',
      occupancy: 'Ocupación',
      availableM2: 'm² disponibles',
      expiring: 'Vencen en ≤ 90 días',
    },

    picker: {
      title: 'Selecciona el inmueble',
      subtitle: 'Busca por nombre o ubicación.',
      close: 'Cerrar',
      searchPlaceholder: 'Nombre del inmueble…',
      location: 'Ubicación',
      allLocations: 'Todas',
      units: '{total} locales',
      available: '{total} disponibles',
      digitized: 'Plano digitalizado',
      outOfScope: 'Fuera del MVP',
      empty: 'Ningún inmueble coincide con la búsqueda.',
    },

    unit: {
      area: 'Superficie',
      contractArea: 'Superficie del contrato',
      ofContract: 'del contrato',
      monthlyRent: 'Renta mensual',
      minimumPrice: 'Precio mínimo',
      minimumPricePerMonth: 'Precio mínimo por mes',
      leaseEnd: 'Vigencia',
      month: 'mes',
      expiresIn: 'Vence en {days} días',
      showPrice: 'Mostrar precio',
      hidePrice: 'Ocultar precio',
      hiddenPrice: '••••••',
    },

    panel: {
      availableOnLevel: 'Disponibles en este nivel',
      availableUnit: 'Local disponible',
      unit: 'Local',
      contract: 'Contrato',
      contractNote: 'Este contrato agrupa {count} locales',
      totalArea: 'Metraje total',
      backToAvailable: 'Ver todos los disponibles',
      selectedEyebrow: 'LOCAL SELECCIONADO',
      deselect: 'Deseleccionar local',
      noneAvailable: 'No hay locales disponibles en este nivel.',
    },

    sheet: {
      eyebrow: 'LOCAL',
      unitNumber: 'Número de local',
      area: 'Metros cuadrados',
      tenant: 'Marca',
    },
  },

  common: {
    loading: 'Cargando…',
    retry: 'Reintentar',
    cancel: 'Cancelar',
    save: 'Guardar',
    search: 'Buscar',
    empty: 'No hay información para mostrar.',
    actions: 'Acciones',
    edit: 'Editar',
    back: 'Regresar',
    inDevelopment: 'En construcción',
    delete: 'Eliminar',
    close: 'Cerrar',
    all: 'Todos',
  },

  errors: {
    generic: 'Ocurrió un error inesperado. Intenta de nuevo.',
    network: 'No se pudo conectar con el servidor.',
    timeout: 'El servidor tardó demasiado en responder. Intenta de nuevo.',
    unauthorized: 'Tu sesión expiró. Inicia sesión de nuevo.',
    notFound: {
      title: 'Página no encontrada',
      message: 'La dirección que abriste no existe en esta herramienta.',
    },
    forbidden: {
      title: 'No tienes acceso',
      message:
        'Tu cuenta no está dada de alta en esta herramienta, o tu rol no incluye este permiso. Solicita el acceso al administrador.',
    },
  },

  admin: {
    users: {
      title: 'Usuarios',
      description:
        'Da de alta un correo institucional y asígnale un rol. La cuenta vive en Entra ID; aquí solo se autoriza su acceso a esta herramienta. Quien no esté dado de alta no puede entrar.',
      create: 'Dar de alta usuario',
      searchPlaceholder: 'Buscar por nombre o correo…',
      allRoles: 'Todos los roles',
      allStates: 'Todos los estados',
      count: 'Mostrando {count}',
      user: 'Usuario',
      role: 'Rol',
      properties: 'Plazas',
      allProperties: 'Todas las plazas',
      noProperties: 'Sin plazas',
      state: 'Estado',
      lastLogin: 'Último acceso',
      active: 'Activo',
      inactive: 'Inactivo',
      neverLoggedIn: 'Nunca ha entrado',
      loadError: 'No se pudo cargar la lista de usuarios.',
      deactivate: 'Desactivar',
      deactivateConfirm: {
        title: 'Desactivar usuario',
        message: '¿Desactivar a {name}? Ya no podrá entrar a la herramienta; su historial se conserva.',
        confirm: 'Desactivar',
      },
      deactivatedToast: 'Usuario desactivado.',
      createdToast: 'Usuario dado de alta.',
      updatedToast: 'Usuario actualizado.',
      form: {
        createTitle: 'Dar de alta usuario',
        editTitle: 'Editar usuario',
        email: 'Correo institucional',
        emailPlaceholder: "nombre{'@'}gigante.com.mx",
        displayName: 'Nombre',
        displayNameHint: 'Opcional: se completa con el de Microsoft en el primer acceso.',
        role: 'Rol',
        rolePlaceholder: 'Elige un rol',
        active: 'Activo',
        allProperties: 'Todas las plazas',
        propertiesLabel: 'Plazas con acceso',
        noPropertiesAvailable: 'No hay plazas disponibles.',
        errors: {
          emailRequired: 'Escribe el correo.',
          emailInvalid: 'El correo no es válido.',
          roleRequired: 'Elige un rol.',
          propertiesRequired: 'Elige al menos una plaza o activa "Todas las plazas".',
        },
      },
    },

    roles: {
      title: 'Roles y permisos',
      description:
        'Un rol agrupa permisos. El catálogo de permisos crece con el sistema: cada funcionalidad nueva declara el permiso que exige.',
      create: 'Crear rol',
      userCount: '{count} usuarios',
      permissionsOf: 'Permisos del rol',
      permissionCount: '{granted} de {total} permisos',
      note: 'El backend verifica estos permisos en cada endpoint. Ocultar una opción en pantalla no protege nada: la decisión real está del lado del servidor.',
      system: 'Sistema',
      loadError: 'No se pudo cargar la lista de roles.',
      empty: 'Todavía no hay roles.',
      deleteConfirm: {
        title: 'Eliminar rol',
        message: '¿Eliminar el rol "{name}"? Esta acción no se puede deshacer.',
        confirm: 'Eliminar',
      },
      deletedToast: 'Rol eliminado.',
      createdToast: 'Rol creado.',
      updatedToast: 'Rol actualizado.',
      systemLocked: 'Los roles de sistema no se pueden renombrar ni eliminar.',
      form: {
        createTitle: 'Crear rol',
        editTitle: 'Editar rol',
        name: 'Nombre',
        namePlaceholder: 'Ej. gerente_comercial',
        description: 'Descripción',
        permissions: 'Permisos',
        errors: {
          nameRequired: 'Escribe el nombre del rol.',
          nameTooLong: 'El nombre es demasiado largo (máximo 60 caracteres).',
          permissionsRequired: 'Elige al menos un permiso.',
        },
      },
    },

    permissions: {
      objects: {
        property: 'Plazas',
        map: 'Mapas',
        unit: 'Locales',
        price: 'Precios',
        plan: 'Planos',
        user: 'Usuarios',
        role: 'Roles',
        audit: 'Bitácora',
      },
      actions: {
        create: 'Crear',
        read: 'Ver',
        update: 'Editar',
        delete: 'Eliminar',
      },
      // Specific wording where "Ver Precios" would not say enough.
      labels: {
        property: { read: 'Ver plazas', create: 'Dar de alta plazas', update: 'Editar plazas' },
        map: { read: 'Ver el mapa de la plaza' },
        unit: { read: 'Ver el detalle de los locales' },
        price: { read: 'Ver renta y precio mínimo' },
        plan: {
          create: 'Subir planos',
          read: 'Ver planos cargados',
          update: 'Publicar planos',
          delete: 'Descartar planos',
        },
        user: { create: 'Dar de alta usuarios', read: 'Ver usuarios', update: 'Editar usuarios', delete: 'Desactivar usuarios' },
        role: { create: 'Crear roles', read: 'Ver roles', update: 'Editar roles', delete: 'Eliminar roles' },
        audit: { read: 'Ver la bitácora' },
      },
    },

    properties: {
      title: 'Plazas',
      description:
        'Catálogo de plazas. Cada plaza se vincula con su unidad de negocio de 8 dígitos, que es la llave con la que se consulta el rent roll en BigQuery.',
      create: 'Nueva plaza',
      empty: 'Todavía no hay plazas registradas.',
      loadError: 'No se pudo cargar la lista de plazas.',
      code: 'Código',
      name: 'Nombre',
      location: 'Ubicación',
      businessUnitCode: 'Unidad de negocio',
      plan: 'Plano',
      published: 'Publicada',
      hasPlan: 'Con plano publicado',
      noPlan: 'Sin plano publicado',
      publishedYes: 'Sí',
      publishedNo: 'No',
      createdToast: 'Plaza creada.',
      updatedToast: 'Plaza actualizada.',
      form: {
        createTitle: 'Nueva plaza',
        editTitle: 'Editar plaza',
        code: 'Código',
        codePlaceholder: 'plaza-norte',
        codeHint: 'Minúsculas, números y guiones. No se puede cambiar después.',
        name: 'Nombre',
        location: 'Ubicación',
        businessUnitCode: 'Unidad de negocio',
        businessUnitPlaceholder: '00000000',
        businessUnitHint: '8 dígitos; se completan con ceros a la izquierda.',
        errors: {
          codeRequired: 'Escribe el código de la plaza.',
          codeInvalid: 'Usa solo minúsculas, números y guiones (por ejemplo plaza-norte).',
          nameRequired: 'Escribe el nombre de la plaza.',
          businessUnitInvalid: 'La unidad de negocio debe tener 8 dígitos.',
        },
      },
    },

    plans: {
      title: 'Planos cargados',
      description:
        'Sube un DWG o DXF, revísalo contra el rent roll y publícalo. El plano aporta la geometría y el código del local; el estatus y los datos vienen siempre del rent roll.',
      upload: 'Subir plano',
      review: 'Revisar',
      publish: 'Publicar',
      discard: 'Descartar',
      property: 'Plaza',
      allProperties: 'Todas las plazas',
      empty: 'Todavía no hay planos subidos para esta plaza.',
      loadError: 'No se pudo cargar la lista de planos.',
      notConfigured: 'El almacenamiento de planos no está configurado en el servidor',
      notConfiguredHint: 'Falta configurar la base de datos o el bucket de planos en el backend.',
      levelsShapes: '{levels} niveles · {shapes} locales',
      uploadedAt: 'Subido',
      publishedAt: 'Publicado',
      processingHint: 'Convirtiendo el archivo…',
      status: {
        processing: 'Procesando',
        draft: 'Borrador',
        failed: 'Falló',
        published: 'Publicado',
        archived: 'Archivado',
      },
      publishConfirm: {
        title: 'Publicar plano',
        message: 'Publicar "{label}" reemplaza el plano publicado actual de {property}, que pasará a archivado. El visor mostrará esta versión de inmediato.',
        confirm: 'Publicar',
      },
      discardConfirm: {
        title: 'Descartar plano',
        message: '¿Descartar "{label}"? Se archiva; el archivo original se conserva.',
        confirm: 'Descartar',
      },
      publishedToast: 'Plano publicado.',
      export: {
        button: 'Descargar SVG',
        loadingLevels: 'Cargando niveles…',
        noLevels: 'Esta versión no tiene niveles.',
        allLevels: 'Todos los niveles',
        colorize: 'Colorear con disponibilidad (mes más reciente)',
        legendTitle: 'Disponibilidad',
        exportedOn: 'exportado',
        done: 'SVG descargado.',
        doneMany: '{count} archivos SVG descargados.',
        rentRollFallback: 'No se pudo obtener la disponibilidad (BigQuery no disponible o sin datos). El SVG se descarga sin colores.',
        error: 'No se pudo generar el SVG.',
      },
      discardedToast: 'Plano descartado.',
      uploadDialog: {
        title: 'Subir plano',
        property: 'Plaza',
        propertyPlaceholder: 'Elige la plaza',
        label: 'Etiqueta de la versión',
        labelPlaceholder: 'Ej. PLAZA OCT26',
        file: 'Archivo',
        drop: 'Arrastra aquí el DWG o DXF o',
        browse: 'haz clic para elegirlo',
        hint: 'Solo .dwg o .dxf, máximo 100 MB.',
        invalidType: 'Solo se aceptan archivos .dwg o .dxf.',
        tooLarge: 'El archivo pesa más de 100 MB.',
        uploading: 'Subiendo… {percent}%',
        submit: 'Subir',
        done: 'Plano subido. Se está convirtiendo; aparecerá como borrador al terminar.',
        required: 'Elige la plaza, escribe una etiqueta y selecciona un archivo.',
      },
      reviewScreen: {
        title: 'Revisión de plano',
        back: 'Volver a planos',
        notReady: 'Este plano aún no tiene geometría para revisar (estado: {status}).',
        loadingGeometry: 'Cargando la geometría del plano…',
        geometryError: 'No se pudo cargar la geometría del plano.',
        rentRollError: 'No se pudo cargar el rent roll de este mes; el plano se muestra sin cruce.',
        summary: 'Resumen del nivel',
        shapes: 'Locales dibujados',
        matched: 'Cruzados con el rent roll',
        unmatched: 'Sin cruzar',
        unmatchedCodes: 'Códigos del plano sin fila en el rent roll',
        rowsWithoutShape: 'Filas del rent roll sin polígono',
        rowsWithoutShapeNote: 'Considera todos los niveles del plano.',
        none: 'Ninguno',
        andMore: 'y {count} más',
        warnings: 'Avisos de la conversión',
        stats: 'Estadísticas',
        statLevels: 'Niveles',
        statShapes: 'Polígonos',
        statUnlabeledHatches: 'Rellenos sin etiqueta',
        statLabelsWithoutPolygon: 'Etiquetas sin polígono',
        publishedAlready: 'Esta versión ya está publicada.',
      },
    },

    audit: {
      title: 'Bitácora',
      description: 'Registro de toda operación de escritura: quién la hizo, sobre qué y cuándo.',
      when: 'Fecha',
      who: 'Usuario',
      action: 'Acción',
      object: 'Objeto',
      property: 'Plaza',
      filters: {
        rangeSeparator: 'a',
        rangeStart: 'Desde',
        rangeEnd: 'Hasta',
        actor: 'Usuario (correo)',
        action: 'Acción',
        allActions: 'Todas las acciones',
        property: 'Plaza',
        allProperties: 'Todas las plazas',
        clear: 'Limpiar filtros',
      },
      loadError: 'No se pudo cargar la bitácora.',
      empty: 'No hay registros con esos filtros.',
      total: '{count} registros',
      changes: 'Cambios',
      noChanges: 'Sin detalle de cambios.',
      ip: 'IP',
      objectId: 'Id del objeto',
      system: 'Sistema',
      objects: {
        user: 'Usuario',
        role: 'Rol',
        plan: 'Plano',
        floor_plan: 'Plano',
      },
      actions: {
        user: {
          create: 'Alta de usuario',
          update: 'Edición de usuario',
          deactivate: 'Baja de usuario',
          bootstrap: 'Administrador inicial',
          scope: { update: 'Cambio de plazas de usuario' },
        },
        role: {
          create: 'Creación de rol',
          update: 'Edición de rol',
          delete: 'Eliminación de rol',
        },
        plan: {
          upload: 'Carga de plano',
          publish: 'Publicación de plano',
          discard: 'Descarte de plano',
        },
      },
    },
  },

  views: {
    viewer: {
      title: 'Consulta de planos',
      pending: 'El visor de planos se construye en las etapas FE-14 a FE-23.',
    },
    users: {
      title: 'Usuarios',
      pending: 'La administración de usuarios se construye en la etapa FE-7.',
    },
    roles: {
      title: 'Roles y permisos',
      pending: 'La administración de roles se construye en la etapa FE-8.',
    },
    plans: {
      title: 'Planos cargados',
      pending: 'La carga y el mapeo de planos se construyen en las etapas FE-11 a FE-13.',
    },
    properties: {
      title: 'Plazas',
    },
    audit: {
      title: 'Bitácora',
      pending: 'La bitácora de auditoría se construye en la etapa FE-9.',
    },
  },
} as const

export type Messages = typeof es
